import { useMemo } from 'react';
import { i18n } from '@/shared/i18n';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  Spinner,
} from '@monksflow/monks-ui';
import { useQuery } from '@tanstack/react-query';
import { format, formatDistanceToNow } from 'date-fns';
import {
  Archive,
  ArrowLeftRight,
  CircleCheck,
  CircleSlash,
  GitBranch,
  GitMerge,
  History,
  Pencil,
  Plus,
  RotateCcw,
  Trash2,
  X,
  type LucideIcon,
} from 'lucide-react';
import {
  diffVersions,
  previousVersion,
  taxonomyChangeLogQueryOptions,
  type TaxonomyChangeAction,
  type TaxonomyChangeLogEntry,
  type TaxonomyVersion,
} from '@/entities/taxonomy';
import { DiffCountBadge } from '../VersionDiffModal/VersionDiffModal';

export interface HistoryDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taxonomyId: string;
  /** Versions of this taxonomy — used to label an entry with its version number. */
  versions: TaxonomyVersion[];
  /** Fallbacks when the Platform user lookup can't resolve the actor (e.g. local). */
  currentUserId?: string;
  currentUserName?: string | null;
  /** Opens the diff of a version against its predecessor. */
  onSeeChanges: (versionId: string) => void;
}

/** Actions whose entry represents a version's content (eligible for a diff). */
const VERSION_ACTIONS: ReadonlySet<TaxonomyChangeAction> = new Set([
  'created',
  'version_created',
  'published',
]);

/**
 * Visual treatment per audit action: label, icon, and a tinted dot/badge tone.
 *
 * Getters, not values: a resolved label would freeze at import.
 */
// `Partial`, matching both sibling drawers: the action union is a hand-mirror of
// the backend enum, so a value added there and not here is a real possibility and
// the lookup has to be treated as one that can miss.
const ACTION_META: Partial<
  Record<TaxonomyChangeAction, { label: string; icon: LucideIcon; tone: string }>
> = {
  created: {
    get label() {
      return i18n.t('knowledge:taxonomy.historyDrawer.created');
    },
    icon: Plus,
    tone: 'text-emerald-600 dark:text-emerald-400',
  },
  version_created: {
    get label() {
      return i18n.t('knowledge:taxonomy.historyDrawer.newVersion');
    },
    icon: GitBranch,
    tone: 'text-sky-600 dark:text-sky-400',
  },
  published: {
    get label() {
      return i18n.t('knowledge:taxonomy.historyDrawer.published');
    },
    icon: CircleCheck,
    tone: 'text-primary',
  },
  unpublished: {
    get label() {
      return i18n.t('knowledge:taxonomy.historyDrawer.unpublished');
    },
    icon: CircleSlash,
    tone: 'text-amber-600 dark:text-amber-400',
  },
  archived: {
    get label() {
      return i18n.t('knowledge:taxonomy.historyDrawer.archived');
    },
    icon: Archive,
    tone: 'text-amber-600 dark:text-amber-400',
  },
  restored: {
    get label() {
      return i18n.t('knowledge:taxonomy.historyDrawer.restored');
    },
    icon: RotateCcw,
    tone: 'text-sky-600 dark:text-sky-400',
  },
  updated: {
    get label() {
      return i18n.t('knowledge:taxonomy.historyDrawer.updated');
    },
    icon: Pencil,
    tone: 'text-muted-foreground',
  },
  merged: {
    get label() {
      return i18n.t('knowledge:taxonomy.historyDrawer.merged');
    },
    icon: GitMerge,
    tone: 'text-violet-600 dark:text-violet-400',
  },
  deleted: {
    get label() {
      return i18n.t('knowledge:taxonomy.historyDrawer.deleted');
    },
    icon: Trash2,
    tone: 'text-destructive',
  },
};

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Right-hand slide-over showing the taxonomy's audit trail: a reverse-chron
 * timeline of every lifecycle change with actor, timestamp, and the mandatory
 * reason. Entries come from `GET /taxonomies/:id/change-log`; the query only
 * runs while the drawer is open. Actor names are backend-resolved, falling back
 * to the signed-in user (covers envs where the Platform lookup isn't reachable).
 */
export function HistoryDrawer(props: HistoryDrawerProps) {
  const { t } = useTranslation('knowledge');
  const { open, onOpenChange, taxonomyId, versions, currentUserId, currentUserName, onSeeChanges } =
    props;

  const query = useQuery({ ...taxonomyChangeLogQueryOptions(taxonomyId), enabled: open });
  const entries = query.data ?? [];

  const versionById = useMemo(() => {
    const map = new Map<string, TaxonomyVersion>();
    for (const v of versions) map.set(v.id, v);
    return map;
  }, [versions]);

  const actorName = (entry: TaxonomyChangeLogEntry): string =>
    entry.actor?.name ??
    (entry.actorId && entry.actorId === currentUserId
      ? (currentUserName ?? t('taxonomy.historyDrawer.you'))
      : null) ??
    t('taxonomy.historyDrawer.unknownUser');

  return (
    <Drawer direction="right" open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="flex h-full w-full flex-col bg-card sm:!max-w-[440px]">
        <DrawerHeader className="flex flex-row items-center justify-between border-b border-border-light">
          <DrawerTitle className="flex items-center gap-2 text-sm">
            <History className="size-4 text-muted-foreground" aria-hidden />
            {t('taxonomy.historyDrawer.history')}
          </DrawerTitle>
          <DrawerClose
            className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            aria-label={t('taxonomy.historyDrawer.closeHistory')}
          >
            <X className="size-4" />
          </DrawerClose>
        </DrawerHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          {query.isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Spinner />
            </div>
          ) : query.isError ? (
            <p className="py-16 text-center text-sm text-muted-foreground">
              {t('taxonomy.historyDrawer.couldnTLoadHistory')}
            </p>
          ) : entries.length === 0 ? (
            <p className="py-16 text-center text-sm text-muted-foreground">
              {t('taxonomy.historyDrawer.noChangesRecordedYet')}
            </p>
          ) : (
            <ol className="relative flex flex-col">
              {entries.map((entry, i) => {
                // Fallback matching both sibling drawers: the action union is a
                // hand-mirror of the backend enum, so a value added there and
                // not here would otherwise throw mid-render and, with the
                // nearest errorElement on the dashboard route, take the shell.
                const meta = ACTION_META[entry.action] ?? {
                  label: entry.action,
                  icon: History,
                  tone: 'text-muted-foreground',
                };
                const Icon = meta.icon;
                const created = new Date(entry.createdAt);
                const version = entry.versionId ? versionById.get(entry.versionId) : undefined;
                const versionNumber = version?.versionNumber;
                const isLast = i === entries.length - 1;
                // Diff this version against its predecessor (only for entries that
                // represent a version's content, and only when something changed).
                const diff =
                  version && VERSION_ACTIONS.has(entry.action)
                    ? diffVersions(previousVersion(versions, version.id), version)
                    : null;
                const hasChanges = !!diff && !diff.isEmpty;
                return (
                  <li key={entry.id} className="flex gap-3">
                    {/* Rail: icon node + connecting line */}
                    <div className="flex flex-col items-center">
                      <span
                        className={`flex size-7 shrink-0 items-center justify-center rounded-full border border-border-light bg-surface ${meta.tone}`}
                      >
                        <Icon className="size-3.5" aria-hidden />
                      </span>
                      {!isLast && <span className="w-px flex-1 bg-border-light" aria-hidden />}
                    </div>

                    {/* Entry body */}
                    <div className={`min-w-0 flex-1 ${isLast ? 'pb-1' : 'pb-5'}`}>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="text-sm font-medium text-foreground">{meta.label}</span>
                        {versionNumber !== undefined && (
                          <Badge
                            variant="ghost"
                            roundness="full"
                            className="border-border-light text-[10px]"
                          >
                            {t('taxonomy.common.versionLabel', { number: versionNumber })}
                          </Badge>
                        )}
                        <span
                          className="text-xs text-muted-foreground"
                          // eslint-disable-next-line i18next/no-literal-string -- date-fns format pattern, machine input to `format()` rather than copy
                          title={format(created, 'PPpp')}
                        >
                          {formatDistanceToNow(created, { addSuffix: true })}
                        </span>
                        {hasChanges && <DiffCountBadge diff={diff} />}
                      </div>

                      <div className="mt-1 flex items-center gap-1.5">
                        <span className="flex size-4 items-center justify-center rounded-full bg-primary/10 text-[8px] font-semibold text-primary">
                          {initials(actorName(entry))}
                        </span>
                        <span className="truncate text-xs text-muted-foreground">
                          {actorName(entry)}
                        </span>
                      </div>

                      <p className="mt-1.5 rounded-md border border-border-light bg-surface/50 px-2.5 py-1.5 text-xs text-foreground">
                        {entry.reason}
                      </p>

                      {hasChanges && version && (
                        <button
                          type="button"
                          onClick={() => onSeeChanges(version.id)}
                          className="mt-1.5 flex items-center gap-1 text-xs font-medium text-primary transition-colors hover:underline"
                        >
                          <ArrowLeftRight className="size-3.5" />
                          {t('taxonomy.historyDrawer.seeChanges')}
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}

HistoryDrawer.displayName = 'HistoryDrawer';
